import { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { MetricCard } from '../components/ui/MetricCard';
import { fetchApi } from '../api/client';
import type { PaginatedResponse } from '../api/client';
import { useAppContext } from '../context/AppContext';
import { formatDateAbbrMonth as fmtDateAbbr } from '../lib/format';
import { useT } from '../i18n';
import { partyMeta, factionMeta, localizedFullName } from '../lib/partyFactionMeta';
import { ChevronRight, WifiOff, Calendar, Radio, TrendingUp } from 'lucide-react';

// Authoritative party colours — keys match exact councillors.party DB values.
// Mirrors Parties.tsx PARTY_COLOURS so both pages are visually consistent.
const PARTY_COLOURS: Record<string, string> = {
  'SP':                '#cc0000',
  'SVP':               '#3d7c1e',
  'FDP':               '#0067b3',
  'FDP.Die Liberalen': '#0067b3',
  'FDP-Liberale':      '#0067b3',
  'Die Mitte':         '#f0820f',
  'Mitte':             '#f0820f',
  'M-E':               '#f0820f',
  'CVP':               '#f0820f',
  'Grüne':             '#84b414',
  'GRÜNE':             '#84b414',
  'GPS':               '#84b414',
  'GLP':               '#a0c233',
  'glp':               '#a0c233',
  'EVP':               '#E8820C',
  'EDU':               '#8B4513',
  'lega':              '#3D85C8',
  'Lega':              '#3D85C8',
  'MCG':               '#f59e0b',
  'MCR':               '#4A90D9',
  'LDP':               '#60a5fa',
  'Al':                '#9333ea',
  'SD':                '#808080',
};

interface OverviewStats {
  councillors: number;      // all active mandates (NR+SR+BR+BK = 254)
  parliamentarians: number; // NR+SR only (246) — the headline figure
  nr_seats: number;
  sr_seats: number;
  br_seats: number;
  bk_seats: number;
  active_affairs: number;
  affairs_submitted_this_year: number;
  affairs_submitted_last_year: number;
  votes_this_year: number;
  votes_last_year: number;
}

interface SessionRow {
  id: number;
  session_name: string;
  abbreviation: string;
  start_date: string;
  end_date: string;
  vote_count?: number;
  pass_rate?: number | null;
}

// Animates a number from 0 → target when target first becomes non-null.
function useCountUp(target: number | null, duration = 700): number | null {
  const [display, setDisplay] = useState<number | null>(null);
  const rafRef = useRef<number>(0);
  useEffect(() => {
    if (target === null) return;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return display;
}

export function Overview() {
  const { lang, parties, uiLang } = useAppContext();
  const t = useT();
  const navigate = useNavigate();

  // Phase 4.1.3 — fetches go through React Query so cross-page navigations
  // hit cache, parallel fetches dedupe, and refetch-on-focus is automatic.
  const { data: stats = null } = useQuery({
    queryKey: ['overview-stats'],
    queryFn:  () => fetchApi<OverviewStats>('/v1/stats'),
  });

  const { data: session = null } = useQuery({
    queryKey: ['sessions-latest', lang],
    queryFn:  () =>
      fetchApi<PaginatedResponse<SessionRow>>('/v1/sessions', { top: 1, language: lang })
        .then(r => r.results[0] ?? null),
  });

  const { data: votesStats } = useQuery({
    queryKey: ['votes-stats', new Date().getFullYear()],
    queryFn:  () =>
      fetchApi<{ pass_rate: number | null; total_decided: number }>(
        '/v1/votes/stats',
        { year: new Date().getFullYear(), language: 'DE' },
      ),
  });
  const votePassRate = votesStats?.pass_rate;
  const totalDecided = votesStats?.total_decided ?? null;

  const { data: partyData = [] } = useQuery({
    queryKey: ['overview-party-composition', parties.map((p: any) => p.abbreviation).join(',')],
    enabled:  parties.length > 0,
    queryFn:  () =>
      fetchApi<PaginatedResponse<any>>('/v1/councillors', { active: true, top: 1000, language: 'DE' })
        .then(res => {
          const counts: Record<string, number> = {};
          res.results.forEach((c: any) => { counts[c.party] = (counts[c.party] || 0) + 1; });
          return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([pname, val]) => {
              const p = parties.find((x: any) => x.abbreviation === pname || x.name === pname);
              // Standardized naming (PARTY_FACTION_NAMING_STANDARD.md): the bar
              // label is the canonical German abbr; full names (localized) live
              // in the hover tooltip. Faction shown as its F-… form + full name.
              const pm = partyMeta(pname);
              const fm = factionMeta(p?.faction_abbreviation);
              const color = PARTY_COLOURS[pname] ?? PARTY_COLOURS[pm.abbr] ?? '#94a3b8';
              return {
                name: pm.abbr,
                fullName: pm.full,        // { de, fr, it } — resolved per uiLang in the tooltip
                faction:  fm.abbr,        // 'F-…' or '' when unknown
                factionFull: fm.full,     // { de, fr, it }
                value: val,
                itemStyle: { color },
              };
            });
        }),
  });

  const { data: recentSessions = null } = useQuery({
    queryKey: ['sessions-recent', lang],
    queryFn:  () =>
      fetchApi<PaginatedResponse<SessionRow>>('/v1/sessions', { top: 8, language: lang })
        .then(res => res.results.slice().reverse()), // oldest → newest left→right
  });

  const { data: hotBills = null } = useQuery({
    queryKey: ['affairs-hot', lang],
    queryFn:  () =>
      fetchApi<{ results: any[] }>('/v1/affairs/hot', { language: lang, top: 6 })
        .then(r => r.results)
        .catch(() => [] as any[]),
  });

  // Animated KPI counters
  // Headline = parliamentarians (NR+SR = 246), NOT total active mandates
  // (254, which includes the 7 Federal Council + 1 Federal Chancellor). The
  // subtitle still breaks out BR/BK so the executive remains visible.
  const animCouncillors = useCountUp(stats?.parliamentarians ?? null);
  const animAffairs     = useCountUp(stats?.active_affairs ?? null);
  const animVotes       = useCountUp(stats?.votes_this_year ?? null);
  const animPassRate    = useCountUp(
    votePassRate !== undefined && votePassRate !== null ? votePassRate : null
  );

  // Session live/ended status
  const sessionStatus = useMemo(() => {
    if (!session) return null;
    const today = dayjs();
    const start = dayjs(session.start_date);
    const end   = dayjs(session.end_date);
    if (today.isBefore(start))  return { kind: 'upcoming' as const, days: start.diff(today, 'day') };
    if (today.isAfter(end))     return { kind: 'ended'    as const, days: today.diff(end,   'day') };
    return                               { kind: 'live'    as const, days: 0 };
  }, [session]);

  // Pass rate delta vs. the most recent past session
  const passRateDelta = useMemo(() => {
    if (!recentSessions || recentSessions.length < 2) return null;
    // recentSessions is oldest→newest; last two items are most recent sessions
    const curr = recentSessions[recentSessions.length - 1];
    const prev = recentSessions[recentSessions.length - 2];
    if (curr.pass_rate == null || prev.pass_rate == null) return null;
    const diff = curr.pass_rate - prev.pass_rate;
    if (diff === 0) return null;
    return `${diff > 0 ? '+' : ''}${diff}% ${t('overview.vsLastSession')}`;
  }, [recentSessions, t]);

  const lastYear = new Date().getFullYear() - 1;

  // Votes delta: this year vs. last year
  const votesDelta = useMemo(() => {
    if (!stats?.votes_last_year || !stats.votes_this_year) return null;
    const pct = Math.round((stats.votes_this_year - stats.votes_last_year) / stats.votes_last_year * 100);
    return `${pct > 0 ? '+' : ''}${pct}% ${t('overview.vsYear').replace('{year}', String(lastYear))}`;
  }, [stats, lastYear, t]);

  // Affairs delta: submitted this year vs. last year
  const affairsDelta = useMemo(() => {
    if (!stats?.affairs_submitted_last_year || !stats.affairs_submitted_this_year) return null;
    const pct = Math.round((stats.affairs_submitted_this_year - stats.affairs_submitted_last_year) / stats.affairs_submitted_last_year * 100);
    return `${pct > 0 ? '+' : ''}${pct}% ${t('overview.vsYear').replace('{year}', String(lastYear))}`;
  }, [stats, lastYear, t]);

  // ── Chart options ────────────────────────────────────────────────────────────
  const lPassed    = t('votes.passed');
  const lRejected  = t('votes.rejected');
  const lPassRate  = t('votes.passRate');
  const lSeats     = t('overview.seats');
  const lFaction   = t('votes.factionCol');
  const lOpenSess  = t('overview.chart.clickToOpenSession');

  // Stacked bar: passed vs rejected per session (last 8, oldest → newest)
  const sessionVotesOption = useMemo(() => {
    const sessions = recentSessions ?? [];
    const labels   = sessions.map(s => s.abbreviation || s.session_name.split(' ').pop() || '');
    const passed   = sessions.map(s => {
      if (!s.vote_count) return 0;
      return Math.round(s.vote_count * (s.pass_rate ?? 0) / 100);
    });
    const rejected = sessions.map((s, i) => (s.vote_count ?? 0) - passed[i]);
    return {
      grid: { left: 12, right: 12, top: 16, bottom: 36, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { fontSize: 10, color: '#A8A49C' },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#E2E0D8', type: 'dashed' } },
        axisLabel: { fontSize: 10, color: '#A8A49C' },
        minInterval: 1,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#fff',
        borderColor: '#E2E0D8',
        borderWidth: 1,
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#1a1917' },
        formatter: (params: any[]) => {
          const p = params[0]?.value ?? 0;
          const r = params[1]?.value ?? 0;
          const total = p + r;
          const rate  = total > 0 ? Math.round(100 * p / total) : 0;
          const s = sessions[params[0]?.dataIndex ?? 0];
          return [
            `<div style="font-weight:700;font-size:13px;margin-bottom:8px">${params[0]?.name}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:20px;margin-bottom:3px">`,
            `  <span style="color:#2E7D32">✓ ${lPassed}</span><span style="font-weight:600">${p}</span>`,
            `</div>`,
            `<div style="display:flex;justify-content:space-between;gap:20px;margin-bottom:3px">`,
            `  <span style="color:#CC0000">✗ ${lRejected}</span><span style="font-weight:600">${r}</span>`,
            `</div>`,
            `<div style="border-top:1px solid #E2E0D8;margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;gap:20px">`,
            `  <span style="color:#A8A49C">${lPassRate}</span><span style="font-weight:700;color:#2E7D32">${rate}%</span>`,
            `</div>`,
            s?.start_date
              ? `<div style="font-size:10px;color:#A8A49C;margin-top:6px">${s.start_date.substring(0, 10)} – ${s.end_date?.substring(0, 10)}</div>`
              : '',
            `<div style="font-size:10px;color:#A8A49C;margin-top:4px;display:flex;align-items:center;gap:3px">${lOpenSess}<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></div>`,
          ].join('');
        },
      },
      legend: {
        bottom: 2,
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: { fontSize: 11, color: '#64748b' },
        data: [lPassed, lRejected],
      },
      series: [
        {
          name: lPassed,
          type: 'bar',
          stack: 'votes',
          data: passed,
          barMaxWidth: 44,
          itemStyle: { color: '#2E7D32' },
          emphasis: { itemStyle: { color: '#1b5e20' } },
        },
        {
          name: lRejected,
          type: 'bar',
          stack: 'votes',
          data: rejected,
          barMaxWidth: 44,
          itemStyle: { color: '#CC0000', borderRadius: [3, 3, 0, 0] },
          emphasis: { itemStyle: { color: '#990000', borderRadius: [3, 3, 0, 0] } },
        },
      ],
    };
  }, [recentSessions, lPassed, lRejected, lPassRate, lOpenSess]);

  // Click a session bar → navigate to that session
  const sessionChartEvents = useMemo(() => ({
    click: (params: any) => {
      const s = recentSessions?.[params.dataIndex];
      if (s) navigate(`/sessions/${s.id}`);
    },
  }), [recentSessions, navigate]);

  // Horizontal bar: party seat counts (top 12, largest at top)
  const partyBarOption = useMemo(() => {
    const total = partyData.reduce((s, p) => s + p.value, 0) || 1;
    const top12 = partyData.slice(0, 12).slice().reverse(); // largest at top
    return {
      grid: { left: 8, right: 52, top: 4, bottom: 4, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { show: false },
        splitLine: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: top12.map(p => p.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { fontSize: 11, color: '#64748b', fontWeight: 500 },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: '#fff',
        borderColor: '#E2E0D8',
        borderWidth: 1,
        padding: [10, 14],
        formatter: (p: any) => {
          const entry = top12.find(x => x.name === p.name);
          const pct   = ((p.value / total) * 100).toFixed(1);
          const color = entry?.itemStyle.color ?? '#94a3b8';
          // Hover reveals the complete localized names (party + Fraktion); the
          // bar label carries only the standardized abbreviation.
          const partyFull   = entry ? localizedFullName(entry.fullName, uiLang) : '';
          const factionFull = entry ? localizedFullName(entry.factionFull, uiLang) : '';
          return [
            `<div style="min-width:170px">`,
            `  <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">`,
            `    <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0"></span>`,
            `    <span style="font-weight:700;font-size:13px;color:#1a1917">${p.name}</span>`,
            `  </div>`,
            partyFull && partyFull !== p.name
              ? `  <div style="font-size:11px;color:#64748b;margin-bottom:8px;padding-left:16px">${partyFull}</div>`
              : '',
            `  <div style="display:flex;justify-content:space-between;gap:20px;margin-bottom:3px">`,
            `    <span style="font-size:11px;color:#A8A49C">${lSeats}</span>`,
            `    <span style="font-weight:700;font-size:13px">${p.value} <span style="font-weight:400;font-size:10px;color:#A8A49C">(${pct}%)</span></span>`,
            `  </div>`,
            entry?.faction
              ? [
                  `  <div style="display:flex;justify-content:space-between;gap:20px;margin-top:2px">`,
                  `    <span style="font-size:11px;color:#A8A49C">${lFaction}</span>`,
                  `    <span style="font-size:11px;font-weight:500">${entry.faction}${factionFull ? ` · ${factionFull}` : ''}</span>`,
                  `  </div>`,
                ].join('')
              : '',
            `</div>`,
          ].join('');
        },
      },
      series: [{
        type: 'bar',
        data: top12.map(p => ({
          value: p.value,
          itemStyle: { color: p.itemStyle.color, borderRadius: [0, 3, 3, 0] },
        })),
        barMaxWidth: 18,
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          color: '#A8A49C',
          formatter: (p: any) => {
            const pct = ((p.value / total) * 100).toFixed(0);
            return `${p.value} (${pct}%)`;
          },
        },
      }],
    };
  }, [partyData, lSeats, lFaction, uiLang]);

  return (
    <div className="flex flex-col gap-[var(--s8)]">

      {/* Header + session status badge */}
      <div>
        <h1 className="text-[28px] font-semibold text-[var(--text-1)] mb-[var(--s2)]">
          {t('overview.title')}
        </h1>
        <div className="flex items-center gap-[10px] flex-wrap">
          <p className="text-[14px] text-[var(--text-2)]">{t('overview.subtitle')}</p>
          {sessionStatus && (
            <span
              className="inline-flex items-center gap-[5px] text-[11px] font-medium px-[8px] py-[3px] rounded-full border"
              style={sessionStatus.kind === 'live'
                ? { background: 'rgba(46,125,50,0.1)', color: '#2E7D32', borderColor: 'rgba(46,125,50,0.3)' }
                : { background: 'var(--surface-2)', color: 'var(--text-3)', borderColor: 'var(--border)' }
              }
            >
              {sessionStatus.kind === 'live' ? (
                <><span className="w-[6px] h-[6px] rounded-full bg-[#2E7D32] animate-pulse inline-block" />{session?.session_name} — {t('overview.sessionLive')}</>
              ) : sessionStatus.kind === 'ended' ? (
                <><WifiOff size={10} />{session?.session_name} — {t('overview.sessionEnded')} {sessionStatus.days}d</>
              ) : (
                <><Calendar size={10} />{session?.session_name} — {t('overview.sessionUpcoming')} {sessionStatus.days}d</>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Live session banner */}
      {sessionStatus?.kind === 'live' && session && (
        <button
          onClick={() => navigate(`/sessions/${session.id}`)}
          className="flex items-center gap-[12px] px-[20px] py-[14px] rounded-[var(--r2)] border border-red-500/30 bg-red-500/8 hover:bg-red-500/12 transition-colors text-left w-full"
        >
          <span className="relative flex items-center justify-center shrink-0">
            <span className="absolute inline-flex h-[20px] w-[20px] rounded-full bg-red-500/30 animate-ping" />
            <Radio size={14} className="relative text-red-500" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-red-500">{t('sessions.liveBannerTitle')}</p>
            <p className="text-[12px] text-[var(--text-2)] truncate">
              {session.session_name} &middot; {t('sessions.liveBannerEnds')} {fmtDateAbbr(session.end_date, uiLang)}
            </p>
          </div>
          <ChevronRight size={14} className="text-red-500 shrink-0" />
        </button>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[var(--s4)] items-stretch">
        <div className="cursor-pointer h-full" onClick={() => navigate('/members')}>
          <MetricCard
            label={t('overview.councillors')}
            value={animCouncillors ?? null}
            subtitle={stats ? t('overview.nrSrSplit').replace('{nr}', String(stats.nr_seats)).replace('{sr}', String(stats.sr_seats)).replace('{br}', String(stats.br_seats)).replace('{bk}', String(stats.bk_seats)) : null}
            isPrimary
          />
        </div>
        <div className="cursor-pointer h-full" onClick={() => navigate('/bills')}>
          <MetricCard
            label={t('overview.activeAffairs')}
            value={animAffairs ?? null}
            subtitle={stats?.affairs_submitted_this_year != null ? t('overview.submittedThisYear').replace('{n}', stats.affairs_submitted_this_year.toLocaleString()) : null}
            delta={affairsDelta ?? undefined}
          />
        </div>
        <div className="cursor-pointer h-full" onClick={() => navigate('/votes')}>
          <MetricCard
            label={t('overview.votesThisYear')}
            value={animVotes ?? null}
            subtitle={t('overview.allChambers')}
            delta={votesDelta ?? undefined}
          />
        </div>
        <div className="cursor-pointer h-full" onClick={() => navigate('/votes')}>
          <MetricCard
            label={t('overview.passRate')}
            value={
              votePassRate === undefined ? null
              : animPassRate !== null ? `${animPassRate}%`
              : '—'
            }
            subtitle={totalDecided !== null ? t('overview.ofVotes').replace('{n}', totalDecided.toLocaleString()) : null}
            delta={passRateDelta ?? undefined}
          />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-[var(--s6)]">

        {/* Votes per session — stacked bar (clickable) */}
        <div className="lg:col-span-2 flex flex-col gap-[var(--s3)]">
          <div className="flex items-center justify-between">
            <h2 className="text-[13px] font-medium font-[family-name:var(--font-mono)] text-[var(--text-3)] uppercase tracking-[0.08em]">
              {t('overview.votesBySession')}
            </h2>
            <button onClick={() => navigate('/sessions')} className="flex items-center gap-[3px] text-[11px] text-[var(--accent)] hover:underline">
              {t('overview.viewAll')} <ChevronRight size={11} />
            </button>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--r2)] shadow-[var(--shadow-sm)] overflow-hidden">
            {recentSessions === null ? (
              <div className="h-[340px] animate-pulse bg-[var(--surface-2)]" />
            ) : (
              <ReactECharts
                option={sessionVotesOption}
                style={{ height: 340, width: '100%', cursor: 'pointer' }}
                onEvents={sessionChartEvents}
              />
            )}
          </div>
        </div>

        {/* Party seats — horizontal bars */}
        <div className="flex flex-col gap-[var(--s3)]">
          <div className="flex items-center justify-between">
            <h2 className="text-[13px] font-medium font-[family-name:var(--font-mono)] text-[var(--text-3)] uppercase tracking-[0.08em]">
              {t('overview.partyComposition')}
            </h2>
            <button onClick={() => navigate('/parties')} className="flex items-center gap-[3px] text-[11px] text-[var(--accent)] hover:underline">
              {t('overview.viewAll')} <ChevronRight size={11} />
            </button>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--r2)] shadow-[var(--shadow-sm)] overflow-hidden">
            {partyData.length === 0 ? (
              <div className="h-[340px] animate-pulse bg-[var(--surface-2)]" />
            ) : (
              <ReactECharts option={partyBarOption} style={{ height: 340, width: '100%' }} />
            )}
          </div>
        </div>
      </div>

      {/* High-Impact Bills feed */}
      {hotBills !== null && hotBills.length > 0 && (
        <div className="flex flex-col gap-[var(--s3)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-[6px]">
              <TrendingUp size={12} className="text-[var(--text-3)]" />
              <h2 className="text-[13px] font-medium font-[family-name:var(--font-mono)] text-[var(--text-3)] uppercase tracking-[0.08em]">
                {t('overview.highImpactBills')}
              </h2>
            </div>
            <button onClick={() => navigate('/bills')} className="flex items-center gap-[3px] text-[11px] text-[var(--accent)] hover:underline">
              {t('overview.viewAll')} <ChevronRight size={11} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[var(--s4)]">
            {hotBills.map((bill: any) => {
              const impact      = bill.impact_rating        ?? 0;
              const sensitivity = bill.political_sensitivity ?? 0;
              const central     = bill.centralization_score  ?? null;
              const composite   = impact * sensitivity;
              const accentColor = composite >= 60 ? '#dc2626' : composite >= 36 ? '#E8820C' : 'var(--accent)';
              return (
                <div
                  key={bill.id}
                  onClick={() => navigate(`/bills/${bill.id}`)}
                  className="relative overflow-hidden flex flex-col gap-[10px] p-[16px] rounded-[var(--r2)] bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-soft)] cursor-pointer transition-colors group"
                  style={{ borderLeft: `3px solid ${accentColor}` }}
                >
                  {/* Top row: number + topic */}
                  <div className="flex items-center justify-between gap-[8px]">
                    <span className="font-[family-name:var(--font-mono)] text-[11px] font-semibold" style={{ color: accentColor }}>
                      {bill.short_number}
                    </span>
                    {bill.primary_topic && (
                      <span className="text-[11px] px-[6px] py-[1px] rounded-full bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-3)] truncate max-w-[130px]">
                        {bill.primary_topic}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <p className="text-[13px] font-medium text-[var(--text-1)] leading-snug line-clamp-2">
                    {bill.title}
                  </p>

                  {/* Summary */}
                  {bill.ai_summary && (
                    <p className="text-[12px] text-[var(--text-2)] leading-relaxed line-clamp-2">
                      {bill.ai_summary}
                    </p>
                  )}

                  {/* Score pills */}
                  <div className="flex items-center gap-[6px] mt-auto pt-[2px] flex-wrap">
                    <span
                      className="inline-flex items-center text-[11px] font-[family-name:var(--font-mono)] px-[6px] py-[2px] rounded-[var(--r1)]"
                      style={{ background: `color-mix(in srgb, ${accentColor} 12%, transparent)`, color: accentColor, border: `1px solid color-mix(in srgb, ${accentColor} 25%, transparent)` }}
                    >
                      {t('common.impact')} {impact.toFixed(1)}
                    </span>
                    <span
                      className="inline-flex items-center text-[11px] font-[family-name:var(--font-mono)] px-[6px] py-[2px] rounded-[var(--r1)]"
                      style={{ background: 'rgba(232,130,12,0.1)', color: '#E8820C', border: '1px solid rgba(232,130,12,0.22)' }}
                    >
                      {t('common.sensitivity')} {sensitivity.toFixed(1)}
                    </span>
                    {bill.intent && (
                      <span className="text-[11px] px-[6px] py-[1px] rounded-full bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-3)] ml-auto truncate max-w-[100px]">
                        {bill.intent}
                      </span>
                    )}
                  </div>

                  {/* Slide-up overlay on hover */}
                  <div
                    className="absolute inset-0 p-[16px] flex flex-col gap-[8px] translate-y-full group-hover:translate-y-0 transition-transform duration-200 ease-out"
                    style={{ background: 'var(--surface)', borderLeft: `3px solid ${accentColor}` }}
                  >
                    {/* Context header */}
                    <div className="flex items-center justify-between gap-[8px]">
                      <span className="font-[family-name:var(--font-mono)] text-[11px] font-semibold" style={{ color: accentColor }}>
                        {bill.short_number}
                      </span>
                      <span className="inline-flex items-center gap-[2px] text-[11px] text-[var(--accent)]">{t('overview.viewDetails')}<ChevronRight size={11} className="shrink-0" /></span>
                    </div>

                    {/* Metadata rows */}
                    <div className="flex flex-col gap-[6px] flex-1">
                      {bill.submitted_by && (
                        <div className="flex gap-[8px] text-[11px]">
                          <span className="text-[var(--text-3)] shrink-0 w-[90px]">{t('common.submittedBy')}</span>
                          <span className="text-[var(--text-1)] font-medium truncate">{bill.submitted_by}</span>
                        </div>
                      )}
                      {bill.responsible_department_name && (
                        <div className="flex gap-[8px] text-[11px]">
                          <span className="text-[var(--text-3)] shrink-0 w-[90px]">{t('common.department')}</span>
                          <span className="text-[var(--text-2)] truncate">{bill.responsible_department_name}</span>
                        </div>
                      )}
                      {bill.status && (
                        <div className="flex gap-[8px] text-[11px]">
                          <span className="text-[var(--text-3)] shrink-0 w-[90px]">{t('common.status')}</span>
                          <span className="text-[var(--text-2)] truncate">{bill.status}</span>
                        </div>
                      )}
                      {central != null && (
                        <div className="flex gap-[8px] text-[11px]">
                          <span className="text-[var(--text-3)] shrink-0 w-[90px]">{t('common.centralization')}</span>
                          <span className="font-[family-name:var(--font-mono)]" style={{ color: 'var(--info)' }}>{central.toFixed(1)} / 10 — {central >= 7 ? t('overview.federalPush') : central >= 4 ? t('overview.balanced') : t('overview.cantonalLean')}</span>
                        </div>
                      )}
                      {bill.external_trigger && (
                        <div className="flex gap-[8px] text-[11px]">
                          <span className="text-[var(--text-3)] shrink-0 w-[90px]">{t('common.trigger')}</span>
                          <span className="text-[var(--text-2)] line-clamp-2">{bill.external_trigger}</span>
                        </div>
                      )}
                    </div>

                    {/* Score pills repeated for context */}
                    <div className="flex items-center gap-[6px] pt-[6px] border-t border-[var(--border)] flex-wrap">
                      <span
                        className="group/tip relative inline-flex items-center text-[11px] font-[family-name:var(--font-mono)] px-[6px] py-[2px] rounded-[var(--r1)] cursor-help"
                        style={{ background: `color-mix(in srgb, ${accentColor} 12%, transparent)`, color: accentColor, border: `1px solid color-mix(in srgb, ${accentColor} 25%, transparent)` }}
                      >
                        {t('common.impact')} {impact.toFixed(1)}
                        <span className="pointer-events-none absolute left-0 bottom-[calc(100%+6px)] z-40 w-[200px] max-w-[calc(100vw-32px)] bg-[var(--surface)] border border-[var(--border)] text-[var(--text-2)] text-[11px] leading-snug p-[8px_10px] rounded-[var(--r2)] shadow-md opacity-0 group-hover/tip:opacity-100 transition-opacity font-[family-name:var(--font-display)] normal-case font-normal whitespace-normal">
                          {t('overview.impactTooltipText')}
                        </span>
                      </span>
                      <span
                        className="group/tip relative inline-flex items-center text-[11px] font-[family-name:var(--font-mono)] px-[6px] py-[2px] rounded-[var(--r1)] cursor-help"
                        style={{ background: 'rgba(232,130,12,0.1)', color: '#E8820C', border: '1px solid rgba(232,130,12,0.22)' }}
                      >
                        {t('common.sensitivity')} {sensitivity.toFixed(1)}
                        <span className="pointer-events-none absolute left-0 bottom-[calc(100%+6px)] z-40 w-[200px] max-w-[calc(100vw-32px)] bg-[var(--surface)] border border-[var(--border)] text-[var(--text-2)] text-[11px] leading-snug p-[8px_10px] rounded-[var(--r2)] shadow-md opacity-0 group-hover/tip:opacity-100 transition-opacity font-[family-name:var(--font-display)] normal-case font-normal whitespace-normal">
                          {t('overview.sensitivityTooltipText')}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
